import React, { useState, useEffect, useRef } from 'react';
import {
    MessageSquare,
    Search,
    User,
    Clock,
    CheckCheck,
    Send,
    Phone,
    RefreshCw,
    Store,
    SearchX,
    Users,
    Plus,
    X,
    Tags,
    FileText,
    Paperclip,
    Download,
    CheckSquare,
    Square,
    Forward,
    Edit2
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { apiClient } from '@dukene/shared';
import { io, Socket } from 'socket.io-client';
import { FlowBuilder } from './FlowBuilder';

interface Contact {
    id: string;
    storeId: string;
    name: string | null;
    phoneNumber: string;
    lastContactedAt: string | null;
    tags?: string[];
    customer?: {
        firstName: string;
        lastName: string;
    };
    store?: Store;
}

interface Message {
    id: string;
    conversationId: string;
    content: string;
    direction: 'INBOUND' | 'OUTBOUND';
    createdAt: string;
    status: 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';
    mediaUrl?: string | null;
    mediaType?: string | null;
    metadata?: {
        whatsappFailure?: { title?: string; message?: string; code?: number } | null;
    };
}

interface Store {
    id: string;
    name: string;
}

interface Conversation {
    id: string;
    status: 'ACTIVE' | 'RESOLVED' | 'ARCHIVED';
    lastMessageAt: string;
    contact: Contact;
    messages: Message[];
    store: Store;
    storeId: string;
    tags: string[];
    _count?: {
        messages: number;
    };
}

interface WhatsAppTag {
    id: string;
    storeId: string;
    name: string;
    color: string;
}

interface Template {
    id: string;
    name: string;
    language: string | { code: string };
    components: any[];
    category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
    header?: string;
    body: string;
    footer?: string;
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    variables?: string[];
    createdAt?: string;
}

const is24HourWindowClosed = (lastContactedAt?: string | null) => {
    if (!lastContactedAt) return true;
    const diff = new Date().getTime() - new Date(lastContactedAt).getTime();
    return diff > 24 * 60 * 60 * 1000;
};

const AdminWhatsAppCRM = () => {
    const [activeTab, setActiveTab] = useState<'inbox' | 'contacts' | 'templates' | 'broadcasts' | 'flows'>(() => {
        return (sessionStorage.getItem('whatsapp_activeTab') as any) || 'inbox';
    });
    
    // Broadcast state
    const [broadcastStoreId, setBroadcastStoreId] = useState<string>('PLATFORM');
    const [broadcastTemplate, setBroadcastTemplate] = useState<Template | null>(null);
    const [broadcastCsv, setBroadcastCsv] = useState<string>('');
    const [broadcastSending, setBroadcastSending] = useState(false);
    
    // Inbox State
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(() => {
        const saved = sessionStorage.getItem('whatsapp_selectedConversation');
        if (saved) {
            try { return JSON.parse(saved); } catch (e) { return null; }
        }
        return null;
    });
    const [messages, setMessages] = useState<Message[]>([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [newMessage, setNewMessage] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'RESOLVED' | 'ARCHIVED'>(() => {
        return (sessionStorage.getItem('whatsapp_statusFilter') as any) || 'ALL';
    });
    const [storeFilter, setStoreFilter] = useState<string>(() => {
        return sessionStorage.getItem('whatsapp_storeFilter') || 'ALL';
    });
    const [stores, setStores] = useState<Store[]>([]);

    useEffect(() => {
        sessionStorage.setItem('whatsapp_activeTab', activeTab);
    }, [activeTab]);

    useEffect(() => {
        if (selectedConversation) {
            sessionStorage.setItem('whatsapp_selectedConversation', JSON.stringify(selectedConversation));
        } else {
            sessionStorage.removeItem('whatsapp_selectedConversation');
        }
    }, [selectedConversation]);

    useEffect(() => {
        sessionStorage.setItem('whatsapp_statusFilter', statusFilter);
    }, [statusFilter]);

    useEffect(() => {
        sessionStorage.setItem('whatsapp_storeFilter', storeFilter);
    }, [storeFilter]);
    const [tagDefinitions, setTagDefinitions] = useState<WhatsAppTag[]>([]);
    
    const [showTagModal, setShowTagModal] = useState(false);
    const [newTagName, setNewTagName] = useState('');
    const [newTagColor, setNewTagColor] = useState('#6b7280');
    
    // Templates State
    const [templates, setTemplates] = useState<Template[]>([]);
    const [selectedTemplateName, setSelectedTemplateName] = useState('');
    const [allTemplates, setAllTemplates] = useState<Template[]>([]);
    const [templateStoreId, setTemplateStoreId] = useState('PLATFORM');
    const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
    const [showTemplatePicker, setShowTemplatePicker] = useState(false);
    const [templateSaving, setTemplateSaving] = useState(false);
    const [templateForm, setTemplateForm] = useState({ name: '', language: 'en', category: 'UTILITY' as Template['category'], header: '', body: '', footer: '', variableExamples: '' });
    
    // Contacts State
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [contactsLoading, setContactsLoading] = useState(false);
    const [startingConversationId, setStartingConversationId] = useState<string | null>(null);
    const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
    const [bulkTagInput, setBulkTagInput] = useState('');
    const [showAddContactModal, setShowAddContactModal] = useState(false);
    const [newContactPhone, setNewContactPhone] = useState('');
    const [newContactName, setNewContactName] = useState('');
    const [newContactStoreId, setNewContactStoreId] = useState('');
    const [editingContact, setEditingContact] = useState<Contact | null>(null);
    const [editContactModalOpen, setEditContactModalOpen] = useState(false);

    const MESSAGE_PAGE_SIZE = 50;
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messageFeedRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const loadingOlderRef = useRef(false);
    const activeConversationIdRef = useRef<string | null>(null);
    const rateLimitUntilRef = useRef(0);
    const rateLimitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [hasOlderMessages, setHasOlderMessages] = useState(true);
    const [rateLimitRetryAt, setRateLimitRetryAt] = useState<number | null>(null);

    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedMessageIds, setSelectedMessageIds] = useState<Set<string>>(new Set());
    const [isForwardModalOpen, setIsForwardModalOpen] = useState(false);
    const [forwardTargetId, setForwardTargetId] = useState<string>('');
    const [forwarding, setForwarding] = useState(false);

    const toggleMessageSelection = (messageId: string) => {
        const newSelection = new Set(selectedMessageIds);
        if (newSelection.has(messageId)) {
            newSelection.delete(messageId);
        } else {
            newSelection.add(messageId);
        }
        setSelectedMessageIds(newSelection);
    };

    const handleForward = async () => {
        if (!forwardTargetId || selectedMessageIds.size === 0) return;
        setForwarding(true);
        
        const selectedMsgs = messages
            .filter(m => selectedMessageIds.has(m.id))
            .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            
        const targetConversation = conversations.find(c => c.id === forwardTargetId);
        if (!targetConversation) {
            setForwarding(false);
            return;
        }

        try {
            for (const msg of selectedMsgs) {
                const type = msg.mediaUrl ? (msg.mediaType?.startsWith('image') ? 'image' : msg.mediaType?.startsWith('video') ? 'video' : msg.mediaType?.startsWith('audio') ? 'audio' : 'document') : 'text';
                await apiClient.post(`/whatsapp/messages/${targetConversation.storeId}`, {
                    phoneNumber: targetConversation.contact.phoneNumber,
                    type,
                    message: msg.content || undefined,
                    fileUrl: msg.mediaUrl || undefined,
                });
                await new Promise(resolve => setTimeout(resolve, 500));
            }
            
            setIsForwardModalOpen(false);
            setIsSelectionMode(false);
            setSelectedMessageIds(new Set());
            alert('Messages forwarded successfully');
        } catch (error) {
            console.error('Error forwarding messages:', error);
            alert('Error forwarding messages');
        } finally {
            setForwarding(false);
        }
    };

    const pauseForRateLimit = (error: any): boolean => {
        if (error?.statusCode !== 429) return false;

        const retrySeconds = Math.max(1, Number(error?.retryAfter) || 60);
        const retryAt = Date.now() + retrySeconds * 1000;
        rateLimitUntilRef.current = Math.max(rateLimitUntilRef.current, retryAt);
        setRateLimitRetryAt(rateLimitUntilRef.current);

        if (rateLimitTimerRef.current) clearTimeout(rateLimitTimerRef.current);
        rateLimitTimerRef.current = setTimeout(() => {
            rateLimitUntilRef.current = 0;
            setRateLimitRetryAt(null);
            rateLimitTimerRef.current = null;
        }, Math.max(1, rateLimitUntilRef.current - Date.now()));

        return true;
    };

    const isRateLimited = () => Date.now() < rateLimitUntilRef.current;

    const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
        messagesEndRef.current?.scrollIntoView({ behavior });
    };

    useEffect(() => {
        fetchStores();
        return () => {
            if (rateLimitTimerRef.current) clearTimeout(rateLimitTimerRef.current);
        };
    }, []);

    useEffect(() => {
        if (activeTab === 'inbox') {
            fetchAllConversations();
        } else if (activeTab === 'contacts') {
            fetchContacts();
        } else {
            fetchAllTemplates(templateStoreId);
        }
    }, [activeTab, statusFilter, storeFilter, templateStoreId]);

    // Initial Load of a conversation
    useEffect(() => {
        if (selectedConversation) {
            activeConversationIdRef.current = selectedConversation.id;
            setMessages([]);
            setHasOlderMessages(true);
            setIsSelectionMode(false);
            setSelectedMessageIds(new Set());
            fetchMessages(selectedConversation.id, true);
            fetchTags(selectedConversation.storeId);
        }
    }, [selectedConversation?.id]);

    const [socket, setSocket] = useState<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);

    // 1. Initialize Socket
    useEffect(() => {
        const socketUrl = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1').replace('/api/v1', '');
        const newSocket = io(socketUrl, { transports: ['websocket'], autoConnect: true });
        
        newSocket.on('connect', () => setIsConnected(true));
        newSocket.on('disconnect', () => setIsConnected(false));
        
        setSocket(newSocket);

        return () => { newSocket.disconnect(); };
    }, []);

    // 2. Join Store Rooms when stores load
    useEffect(() => {
        if (socket && isConnected && stores.length > 0) {
            stores.forEach(store => socket.emit('join_store', store.id));
        }
    }, [socket, isConnected, stores]);

    // 3. Listen for incoming messages
    useEffect(() => {
        if (!socket) return;
        
        const handleNewMessage = (newMessage: Message) => {
            // Instantly push to the active conversation view if we are looking at it
            if (activeConversationIdRef.current === newMessage.conversationId) {
                setMessages(current => {
                    if (current.some(m => m.id === newMessage.id)) return current;
                    const merged = [...current, newMessage].sort(
                        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() || a.id.localeCompare(b.id)
                    );
                    
                    const wasNearBottom = messageFeedRef.current
                        ? messageFeedRef.current.scrollHeight - messageFeedRef.current.scrollTop - messageFeedRef.current.clientHeight < 120
                        : true;
                    if (wasNearBottom) setTimeout(() => scrollToBottom('smooth'), 50);
                    
                    return merged;
                });
            }
            // Always refresh the conversations list quietly so snippets update
            fetchAllConversations(false);
        };

        socket.on('new_whatsapp_message', handleNewMessage);
        return () => { socket.off('new_whatsapp_message', handleNewMessage); };
    }, [socket]);

    useEffect(() => {
        if (selectedConversation) {
            fetchTemplates(selectedConversation.storeId);
        }
    }, [selectedConversation]);

    const fetchStores = async () => {
        try {
            const data = await apiClient.get<Store[]>('/stores');
            setStores(data || []);
        } catch (error) {
            console.error('Error fetching stores:', error);
        }
    };

    const fetchAllConversations = async (showLoading = true) => {
        if (isRateLimited()) return;
        if (showLoading && conversations.length === 0) setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter !== 'ALL') params.append('status', statusFilter);
            if (storeFilter !== 'ALL') params.append('storeId', storeFilter);

            const data = await apiClient.get<any>(`/whatsapp/admin/all-conversations?${params}`);
            const sorted = [...(data?.conversations || [])].sort((a: Conversation, b: Conversation) => {
                const rank = (status: Conversation['status']) => status === 'ACTIVE' ? 0 : status === 'RESOLVED' ? 1 : 2;
                return rank(a.status) - rank(b.status) || new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime();
            });
            setConversations(sorted);
        } catch (error) {
            pauseForRateLimit(error);
            console.error('Error fetching conversations:', error);
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    const fetchTags = async (storeId: string) => {
        try {
            setTagDefinitions(await apiClient.get<WhatsAppTag[]>(`/whatsapp/tags/${storeId}`) || []);
        } catch (error) {
            console.error('Error fetching tag definitions:', error);
        }
    };

    const fetchMessages = async (conversationId: string, initial = false) => {
        if (isRateLimited()) return;
        try {
            const data = await apiClient.get<Message[]>(`/whatsapp/conversations/${conversationId}/messages?limit=${MESSAGE_PAGE_SIZE}`);
            if (activeConversationIdRef.current !== conversationId) return;

            const latest = [...(data || [])].reverse();
            if (initial) {
                setMessages(latest);
                setHasOlderMessages((data || []).length === MESSAGE_PAGE_SIZE);
                setTimeout(() => scrollToBottom('auto'), 100);
                return;
            }

            const wasNearBottom = messageFeedRef.current
                ? messageFeedRef.current.scrollHeight - messageFeedRef.current.scrollTop - messageFeedRef.current.clientHeight < 120
                : false;
            setMessages(current => {
                const merged = new Map(current.map(message => [message.id, message]));
                latest.forEach(message => merged.set(message.id, message));
                return Array.from(merged.values()).sort(
                    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() || a.id.localeCompare(b.id)
                );
            });
            if (wasNearBottom) setTimeout(() => scrollToBottom('smooth'), 50);
        } catch (error) {
            pauseForRateLimit(error);
            console.error('Error fetching messages:', error);
        }
    };

    const loadOlderMessages = async () => {
        if (!selectedConversation || !messages.length || !hasOlderMessages || loadingOlderRef.current) return;

        const feed = messageFeedRef.current;
        const previousScrollHeight = feed?.scrollHeight || 0;
        loadingOlderRef.current = true;
        setLoadingOlder(true);
        try {
            const cursor = messages[0].id;
            const data = await apiClient.get<Message[]>(
                `/whatsapp/conversations/${selectedConversation.id}/messages?limit=${MESSAGE_PAGE_SIZE}&cursor=${encodeURIComponent(cursor)}`
            );
            if (activeConversationIdRef.current !== selectedConversation.id) return;

            const older = [...(data || [])].reverse();
            setHasOlderMessages((data || []).length === MESSAGE_PAGE_SIZE);
            setMessages(current => {
                const currentIds = new Set(current.map(message => message.id));
                return [...older.filter(message => !currentIds.has(message.id)), ...current];
            });
            requestAnimationFrame(() => {
                if (feed) feed.scrollTop = feed.scrollHeight - previousScrollHeight;
            });
        } catch (error) {
            console.error('Error fetching older messages:', error);
        } finally {
            loadingOlderRef.current = false;
            setLoadingOlder(false);
        }
    };

    const handleMessageFeedScroll = () => {
        if ((messageFeedRef.current?.scrollTop || 0) <= 80) loadOlderMessages();
    };

    const fetchTemplates = async (storeId: string) => {
        try {
            const res = await apiClient.get<Template[]>(`/whatsapp/templates/${storeId}`);
            setTemplates((res || []).filter(template => template.status === 'APPROVED').slice(0, 10));
        } catch (error) {
            console.error('Error fetching templates:', error);
        }
    };

    const fetchAllTemplates = async (storeId: string) => {
        try {
            const res = await apiClient.get<Template[]>(`/whatsapp/template/${storeId}`);
            setAllTemplates(res || []);
        } catch (error) {
            console.error('Error fetching templates:', error);
        }
    };

    const syncTemplatesWithMeta = async (storeId: string) => {
        try {
            // First call the sync endpoint to pull latest statuses from Meta
            await apiClient.get(`/whatsapp/template/${storeId}/sync`);
            // Then fetch from our DB
            await fetchAllTemplates(storeId);
        } catch (error) {
            console.error('Error syncing templates with Meta:', error);
            alert('Failed to sync templates with Meta. Please check your configuration.');
        }
    };

    const createMessageTemplate = async () => {
        if (!templateForm.name.trim() || !templateForm.body.trim() || !templateStoreId) return;
        setTemplateSaving(true);
        try {
            await apiClient.post(`/whatsapp/template/${templateStoreId}`, {
                ...templateForm,
                name: templateForm.name.trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_'),
                header: templateForm.header.trim() || undefined,
                footer: templateForm.footer.trim() || undefined,
                variableExamples: templateForm.variableExamples.split(',').map(value => value.trim()).filter(Boolean),
            });
            setTemplateForm({ name: '', language: 'en', category: 'UTILITY', header: '', body: '', footer: '', variableExamples: '' });
            setShowCreateTemplateModal(false);
            await fetchAllTemplates(templateStoreId);
            alert('Template submitted to Meta for approval.');
        } catch (error: any) {
            console.error('Error creating template:', error);
            alert(`Failed to create template: ${error?.message || 'Please try again.'}`);
        } finally {
            setTemplateSaving(false);
        }
    };

    const sendBroadcast = async () => {
        if (!broadcastTemplate || !broadcastCsv.trim()) return;
        setBroadcastSending(true);
        
        try {
            // Very simple CSV parser: split by newlines, then by commas
            const lines = broadcastCsv.split('\n').filter(line => line.trim());
            const recipients = lines.map(line => {
                const parts = line.split(',').map(p => p.trim());
                return {
                    phone: parts[0],
                    variables: parts.slice(1) // everything after phone
                };
            }).filter(r => r.phone); // Require phone number

            await apiClient.post(`/whatsapp/broadcast/${broadcastStoreId}`, {
                campaignName: `Broadcast ${broadcastTemplate.name} ${new Date().toLocaleDateString()}`,
                templateName: broadcastTemplate.name,
                language: typeof broadcastTemplate.language === 'string' ? broadcastTemplate.language : broadcastTemplate.language.code,
                recipients
            });
            
            setBroadcastCsv('');
            setBroadcastTemplate(null);
            alert(`Broadcast started successfully for ${recipients.length} recipients!`);
        } catch (error: any) {
            console.error('Error starting broadcast:', error);
            alert(`Failed to start broadcast: ${error?.message || 'Please try again.'}`);
        } finally {
            setBroadcastSending(false);
        }
    };

    const fetchContacts = async () => {
        if (contacts.length === 0) setContactsLoading(true);
        try {
            const res = await apiClient.get<any>('/whatsapp/admin/all-contacts?limit=50&offset=0');
            setContacts(res?.contacts || []);
        } catch (error) {
            console.error('Error fetching contacts:', error);
        } finally {
            setContactsLoading(false);
        }
    };

    const startContactConversation = async (contact: Contact) => {
        setStartingConversationId(contact.id);
        try {
            const conversation = await apiClient.post<Conversation>(
                `/whatsapp/conversations/contact/${contact.id}`,
                {}
            );
            setStatusFilter('ALL');
            setStoreFilter(contact.storeId);
            setSelectedConversation(conversation);
            setConversations(current => [
                conversation,
                ...current.filter(item => item.id !== conversation.id),
            ]);
            setActiveTab('inbox');
        } catch (error: any) {
            console.error('Error starting conversation:', error);
            alert(`Unable to start conversation: ${error?.message || 'Please try again.'}`);
        } finally {
            setStartingConversationId(null);
        }
    };

    const sendMessage = async () => {
        if (!newMessage.trim() || !selectedConversation) return;
        if (isRateLimited()) {
            alert('Messaging is temporarily paused by the server. Please wait for the displayed retry time.');
            return;
        }

        setSending(true);
        const content = newMessage.trim();
        const storeId = selectedConversation.storeId;
        const phoneNumber = selectedConversation.contact.phoneNumber;

        try {
            await apiClient.post(`/whatsapp/messages/${storeId}`, {
                phoneNumber,
                message: content,
                type: 'text',
            });
            
            setNewMessage('');
            await fetchMessages(selectedConversation.id);
        } catch (error: any) {
            console.error('Error sending message:', error);
            if (pauseForRateLimit(error)) {
                const retrySeconds = Math.max(1, Number(error?.retryAfter) || 60);
                alert(`Too many requests reached the server. Messaging will retry after about ${retrySeconds} seconds.`);
            } else {
                alert(`Failed to send message: ${error?.message || 'Please try again.'}`);
            }
        } finally {
            setSending(false);
        }
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setSelectedFile(file);
        
        // Create object URL for preview if it's an image
        if (file.type.startsWith('image/')) {
            setPreviewUrl(URL.createObjectURL(file));
        } else {
            setPreviewUrl(null); // No preview for docs, we'll just show the name
        }
        
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const clearFilePreview = () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setSelectedFile(null);
        setPreviewUrl(null);
    };

    const sendFileMessage = async () => {
        if (!selectedFile || !selectedConversation) return;
        if (isRateLimited()) {
            alert('Messaging is temporarily paused by the server. Please wait for the displayed retry time.');
            return;
        }

        setSending(true);
        try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('folder', 'whatsapp');

            const uploadedMedia = await apiClient.post<any>('/uploads/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const type = selectedFile.type.startsWith('image/') ? 'image' : 
                         selectedFile.type.startsWith('video/') ? 'video' : 
                         selectedFile.type.startsWith('audio/') ? 'audio' : 'document';
                         
            await apiClient.post(`/whatsapp/messages/${selectedConversation.storeId}`, {
                phoneNumber: selectedConversation.contact.phoneNumber,
                type: type,
                fileUrl: uploadedMedia.url,
                fileName: selectedFile.name,
                caption: newMessage.trim() || undefined,
            });
            
            setNewMessage('');
            clearFilePreview();
            await fetchMessages(selectedConversation.id);
        } catch (error: any) {
            console.error('Error uploading file:', error);
            alert(`Failed to send file: ${error?.message || 'Please try again.'}`);
        } finally {
            setSending(false);
        }
    };

    const sendTemplate = async (templateToSend?: Template) => {
        if (!selectedConversation) return;
        if (isRateLimited()) {
            alert('Template sending is temporarily paused by the server. Please wait for the displayed retry time.');
            return;
        }
        const template = templateToSend || templates.find(t => t.name === selectedTemplateName);
        if (!template) return;

        const variableValues: string[] = [];
        for (const variable of template.variables || []) {
            const value = window.prompt(`Enter a value for ${variable}:`);
            if (value === null) return;
            if (!value.trim()) {
                alert(`${variable} cannot be empty.`);
                return;
            }
            variableValues.push(value.trim());
        }
        const components = variableValues.length > 0 ? [{
            type: 'body',
            parameters: variableValues.map(text => ({ type: 'text', text })),
        }] : (template.components || []);

        setSending(true);
        try {
            await apiClient.post(`/whatsapp/messages/${selectedConversation.storeId}`, {
                phoneNumber: selectedConversation.contact.phoneNumber,
                type: 'template',
                template: {
                    name: template.name,
                    language: typeof template.language === 'string' ? { code: template.language } : template.language || { code: 'en' },
                    components
                }
            });
            setSelectedTemplateName('');
            setShowTemplatePicker(false);
            await fetchMessages(selectedConversation.id);
            // A business-initiated template does not open Meta's 24-hour window.
            await fetchAllConversations(false);
        } catch (error: any) {
            console.error('Error sending template:', error);
            if (pauseForRateLimit(error)) {
                const retrySeconds = Math.max(1, Number(error?.retryAfter) || 60);
                alert(`Too many requests reached the server. Template sending will be available in about ${retrySeconds} seconds.`);
            } else {
                alert(`Failed to send template: ${error?.message || 'Please try again.'}`);
            }
        } finally {
            setSending(false);
        }
    };

    const resolveConversation = async (conversationId: string) => {
        if (!window.confirm('Mark this conversation as resolved?')) return;
        try {
            await apiClient.post(`/whatsapp/conversations/${conversationId}/resolve`, {});
            await fetchAllConversations();
            setSelectedConversation(null);
            setMessages([]);
        } catch (error) {
            console.error('Error resolving conversation:', error);
        }
    };

    const updateConversationTags = async (tags: string[]) => {
        if (!selectedConversation) return;
        try {
            await apiClient.put(`/whatsapp/conversations/${selectedConversation.id}/tags`, { tags });
            const updated = { ...selectedConversation, tags };
            setSelectedConversation(updated);
            setConversations(prev => prev.map(c => c.id === updated.id ? updated : c));
        } catch (error) {
            console.error('Error updating conversation tags:', error);
        }
    };

    const removeTag = async (tagToRemove: string) => {
        if (!selectedConversation) return;
        await updateConversationTags((selectedConversation.tags || []).filter(tag => tag !== tagToRemove));
    };

    const createTag = async () => {
        if (!selectedConversation || !newTagName.trim()) return;
        try {
            await apiClient.post(`/whatsapp/tags/${selectedConversation.storeId}`, {
                name: newTagName.trim(), color: newTagColor,
            });
            setNewTagName('');
            await fetchTags(selectedConversation.storeId);
        } catch (error) {
            console.error('Error creating tag:', error);
            alert('Could not create tag. The name may already exist.');
        }
    };

    const tagColor = (name: string) => tagDefinitions.find(tag => tag.name === name)?.color || '#6b7280';

    const handleAddContact = async () => {
        try {
            await apiClient.post(`/whatsapp/contacts/${newContactStoreId}`, {
                phoneNumber: newContactPhone,
                name: newContactName
            });
            setShowAddContactModal(false);
            setNewContactPhone('');
            setNewContactName('');
            fetchContacts();
        } catch (e: any) {
            console.error('Error adding contact:', e);
            alert(`Failed to add contact: ${e?.message || 'Please try again.'}`);
        }
    };

    const handleEditContact = async () => {
        if (!editingContact) return;
        try {
            await apiClient.put(`/whatsapp/contacts/${editingContact.id}`, {
                name: newContactName,
                tags: editingContact.tags || []
            });
            setEditContactModalOpen(false);
            setEditingContact(null);
            setNewContactName('');
            fetchContacts();
        } catch (e: any) {
            console.error('Error editing contact:', e);
            alert(`Failed to edit contact: ${e?.message || 'Please try again.'}`);
        }
    };

    const handleBulkTag = async () => {
        try {
            await apiClient.patch('/whatsapp/contacts/bulk-tag', {
                contactIds: selectedContactIds,
                tags: [bulkTagInput],
                action: 'add'
            });
            setBulkTagInput('');
            setSelectedContactIds([]);
            fetchContacts();
        } catch(e) {
            console.error('Error bulk tagging:', e);
            alert('Failed to tag contacts');
        }
    };

    const filteredConversations = conversations.filter((conv) =>
        conv.contact.phoneNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        conv.contact.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        conv.contact.customer?.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        conv.contact.customer?.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        conv.store?.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const formatWhatsAppText = (text: string) => {
        if (!text) return null;
        
        // WhatsApp Markdown parsing for *bold*, _italic_, ~strikethrough~
        const parseLine = (line: string, lineIndex: number) => {
            const parts = line.split(/(\*[^*]+\*|_[^_]+_|~[^~]+~)/g);
            return parts.map((part, i) => {
                const key = `${lineIndex}-${i}`;
                if (part.startsWith('*') && part.endsWith('*')) {
                    return <strong key={key} className="font-bold">{part.slice(1, -1)}</strong>;
                }
                if (part.startsWith('_') && part.endsWith('_')) {
                    return <em key={key} className="italic">{part.slice(1, -1)}</em>;
                }
                if (part.startsWith('~') && part.endsWith('~')) {
                    return <del key={key} className="line-through">{part.slice(1, -1)}</del>;
                }
                return part;
            });
        };

        return text.split('\n').map((line, i, arr) => (
            <React.Fragment key={i}>
                {parseLine(line, i)}
                {i < arr.length - 1 && <br />}
            </React.Fragment>
        ));
    };

    const getContactName = (contact: Contact) => {
        if (contact.customer) {
            return `${contact.customer.firstName} ${contact.customer.lastName}`;
        }
        return contact.name || contact.phoneNumber;
    };

    return (
        <div className="h-[calc(100vh-4rem)] flex flex-col bg-white">
            {rateLimitRetryAt && (
                <div className="mx-4 mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[10px] font-semibold text-amber-800">
                    Auto-actions paused until {new Date(rateLimitRetryAt).toLocaleTimeString()}
                </div>
            )}
            {/* Header Section */}
            <div className="bg-white px-4 pt-2">
                <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-4">
                        <h1 className="text-lg font-semibold tracking-tight text-black flex items-center gap-1.5">
                            WhatsApp CRM
                            <span title={isConnected ? 'Live' : 'Connecting...'} className={`flex h-1.5 w-1.5 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-amber-500'}`}></span>
                        </h1>
                        <div className="flex gap-4">
                            <button onClick={() => setActiveTab('inbox')} className={`pb-1 text-xs font-medium border-b transition-all ${activeTab === 'inbox' ? 'border-black text-black' : 'border-transparent text-gray-400 hover:text-black'}`}>Inbox</button>
                            <button onClick={() => setActiveTab('contacts')} className={`pb-1 text-xs font-medium border-b transition-all ${activeTab === 'contacts' ? 'border-black text-black' : 'border-transparent text-gray-400 hover:text-black'}`}>Contacts</button>
                            <button onClick={() => setActiveTab('templates')} className={`pb-1 text-xs font-medium border-b transition-all ${activeTab === 'templates' ? 'border-black text-black' : 'border-transparent text-gray-400 hover:text-black'}`}>Templates</button>
                            <button onClick={() => setActiveTab('broadcasts')} className={`pb-1 text-xs font-medium border-b transition-all ${activeTab === 'broadcasts' ? 'border-black text-black' : 'border-transparent text-gray-400 hover:text-black'}`}>Broadcasts</button>
                            <button onClick={() => setActiveTab('flows')} className={`pb-1 text-xs font-medium border-b transition-all ${activeTab === 'flows' ? 'border-black text-black' : 'border-transparent text-gray-400 hover:text-black'}`}>Flows</button>
                        </div>
                    </div>
                    
                    {activeTab === 'inbox' && (
                        <div className="flex items-center gap-2">
                            {selectedConversation && (
                                <button onClick={() => setShowTagModal(true)} className="h-7 px-2 rounded-md flex items-center gap-1 text-[10px] font-medium border border-gray-200 hover:border-black text-gray-600">
                                    <Tags className="w-3 h-3" /> Tags
                                </button>
                            )}
                            
                            <div className="h-7 flex items-center gap-1 px-1 rounded-md border border-gray-100 bg-gray-50/50">
                                <select
                                    value={storeFilter}
                                    onChange={(e) => setStoreFilter(e.target.value)}
                                    className="h-full px-1 text-[10px] font-medium text-gray-600 border-none focus:ring-0 bg-transparent"
                                >
                                    <option value="ALL">All Stores</option>
                                    <option value="PLATFORM">Platform</option>
                                    {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                                <div className="w-px h-4 bg-gray-200"></div>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value as any)}
                                    className="h-full px-1 text-[10px] font-medium text-gray-600 border-none focus:ring-0 bg-transparent"
                                >
                                    <option value="ALL">All Status</option>
                                    <option value="ACTIVE">Active</option>
                                    <option value="RESOLVED">Resolved</option>
                                    <option value="ARCHIVED">Archived</option>
                                </select>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            <div className="flex-1 flex overflow-hidden">
                {activeTab === 'inbox' ? (
                    <>
                        {/* Conversations Sidebar */}
                        <div className="w-full md:w-[250px] bg-white border-r border-gray-100 flex flex-col">
                            {/* Search Bar */}
                            <div className="p-2 border-b border-gray-100">
                                <div className="relative group">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-black transition-colors" />
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Search..."
                                        className="w-full pl-10 pr-3 py-2 bg-gray-50 border border-transparent rounded-lg focus:border-black focus:bg-white transition-all outline-none text-xs font-medium"
                                    />
                                </div>
                            </div>

                            {/* List */}
                            <div className="flex-1 overflow-y-auto p-2">
                                {loading ? (
                                    <div className="space-y-3">
                                        {[1, 2, 3, 4, 5].map(i => (
                                            <div key={i} className="h-20 bg-gray-50 animate-pulse rounded-2xl"></div>
                                        ))}
                                    </div>
                                ) : filteredConversations.length === 0 ? (
                                    <div className="py-20 text-center">
                                        <SearchX className="w-6 h-6 mx-auto mb-3 text-gray-200" />
                                        <p className="text-sm font-medium text-black">No conversations found</p>
                                    </div>
                                ) : (
                                    <div className="space-y-1">
                                        {filteredConversations.map((conversation) => (
                                            <button
                                                key={conversation.id}
                                                onClick={() => setSelectedConversation(conversation)}
                                                className={`w-full p-2 rounded-xl text-left transition-all duration-200 group relative ${
                                                    selectedConversation?.id === conversation.id 
                                                    ? 'bg-black text-white shadow-lg' 
                                                    : 'hover:bg-gray-50 text-gray-900'
                                                }`}
                                            >
                                                <div className="flex items-start gap-2">
                                                    <div className={`w-6 h-6 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                                                        selectedConversation?.id === conversation.id ? 'bg-gray-800' : 'bg-gray-100'
                                                    }`}>
                                                        <User className={`w-5 h-5 ${selectedConversation?.id === conversation.id ? 'text-white' : 'text-black'}`} />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between mb-0.5">
                                                            <p className="font-semibold text-sm truncate">
                                                                {getContactName(conversation.contact)}
                                                            </p>
                                                            <span className={`text-[10px] font-medium flex-shrink-0 ml-2 ${
                                                                selectedConversation?.id === conversation.id ? 'text-gray-400' : 'text-gray-400'
                                                            }`}>
                                                                {(() => {
                                                                    const d = new Date(conversation.lastMessageAt);
                                                                    const today = new Date();
                                                                    if (d.toDateString() === today.toDateString()) {
                                                                        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                                                    }
                                                                    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
                                                                })()}
                                                            </span>
                                                        </div>
                                                        <p className={`text-xs truncate ${
                                                            selectedConversation?.id === conversation.id ? 'text-gray-300' : 'text-gray-500'
                                                        }`}>
                                                            {conversation.messages[0]?.content || 'No messages yet'}
                                                        </p>
                                                        <div className="flex flex-wrap gap-1 mt-1">
                                                            {conversation.status === 'RESOLVED' && <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-emerald-100 text-emerald-700">RESOLVED</span>}
                                                            {(conversation.tags || []).map(tag => (
                                                                <span key={tag} style={{ backgroundColor: tagColor(tag) }} className="px-1.5 py-0.5 rounded text-[9px] font-medium text-white">{tag}</span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                                {conversation.status === 'ACTIVE' && (
                                                    <div className={`absolute top-3 right-4 w-2 h-2 rounded-full ${
                                                        selectedConversation?.id === conversation.id ? 'bg-white' : 'bg-black'
                                                    }`}></div>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Chat Section */}
                        {selectedConversation ? (
                            <>
                                <div className="flex-1 flex flex-col bg-gray-50/50 relative">
                                    {/* Chat Top Bar */}
                                    {isSelectionMode ? (
                                        <div className="bg-blue-50 border-b border-gray-100 px-4 py-2 flex items-center justify-between shadow-sm sticky top-0 z-10">
                                            <div className="flex items-center gap-3">
                                                <button onClick={() => { setIsSelectionMode(false); setSelectedMessageIds(new Set()); }} className="p-2 hover:bg-blue-100 rounded-lg">
                                                    <X className="w-5 h-5 text-blue-900" />
                                                </button>
                                                <span className="font-medium text-blue-900">{selectedMessageIds.size} Selected</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => setIsForwardModalOpen(true)}
                                                    disabled={selectedMessageIds.size === 0}
                                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                                                >
                                                    <Forward className="w-4 h-4" />
                                                    Forward
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="bg-white border-b border-gray-100 px-4 py-2 flex items-center justify-between shadow-sm sticky top-0 z-10">
                                            <div className="flex items-center gap-2">
                                                <div className="w-6 h-6 bg-black rounded-xl flex items-center justify-center">
                                                    <User className="w-5 h-5 text-white" />
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-black leading-tight">
                                                        {getContactName(selectedConversation.contact)}
                                                    </h3>
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                        <span className="flex items-center gap-1 text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                                                            <Phone className="w-3 h-3" />
                                                            {selectedConversation.contact.phoneNumber}
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        {(selectedConversation.tags || []).map(tag => <span key={tag} style={{ backgroundColor: tagColor(tag) }} className="px-1.5 py-0.5 rounded text-[9px] font-medium text-white">{tag}</span>)}
                                                        {selectedConversation.status === 'RESOLVED' && <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-emerald-100 text-emerald-700">RESOLVED</span>}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => setIsSelectionMode(true)}
                                                    className="px-3 py-1.5 hover:bg-gray-100 rounded-lg text-gray-600 text-sm font-medium flex items-center gap-1.5"
                                                    title="Select Messages"
                                                >
                                                    <CheckSquare className="w-4 h-4" />
                                                    Select
                                                </button>
                                                {selectedConversation.status === 'ACTIVE' && (
                                                    <button
                                                        onClick={() => resolveConversation(selectedConversation.id)}
                                                        className="h-9 px-4 bg-gray-100 text-black hover:bg-gray-200 rounded-xl font-medium text-xs uppercase tracking-widest transition-all"
                                                    >
                                                        Mark Resolved
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Message Feed */}
                                    <div
                                        ref={messageFeedRef}
                                        onScroll={handleMessageFeedScroll}
                                        className="flex-1 overflow-y-auto p-3 space-y-3 bg-[#efeae2]"
                                        style={{
                                            backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'><g fill='%23b4a896' fill-opacity='0.08'><path d='M10 10h2v2h-2zm20 15h2v2h-2zm-15 30h2v2h-2zM50 5h2v2h-2zm20 40h2v2h-2zM5 45h5v2H5zm50 25h5v2h-5zM15 65c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm45-50c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm10 50c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM35 55c0-2.8-2.2-5-5-5s-5 2.2-5 5 2.2 5 5 5 5-2.2 5-5zm25-20c0-2.8-2.2-5-5-5s-5 2.2-5 5 2.2 5 5 5 5-2.2 5-5z'/><path d='M65 15c0-1.7-1.3-3-3-3s-3 1.3-3 3 1.3 3 3 3 3-1.3 3-3zm-45 45c0-1.7-1.3-3-3-3s-3 1.3-3 3 1.3 3 3 3 3-1.3 3-3z' fill='%23b4a896' fill-opacity='0.05'/></g></svg>")`,
                                            backgroundRepeat: 'repeat',
                                            backgroundAttachment: 'scroll',
                                            backgroundSize: '360px auto'
                                        }}
                                    >
                                        {loadingOlder && (
                                            <div className="flex justify-center py-1 text-xs font-medium text-gray-500">
                                                <RefreshCw className="mr-2 h-3.5 w-3.5 animate-spin" />
                                                Loading older messages...
                                            </div>
                                        )}
                                        {!hasOlderMessages && messages.length > 0 && (
                                            <div className="flex justify-center">
                                                <span className="py-1 px-3 bg-[#fdfcec] rounded-lg shadow-sm text-[10px] font-medium uppercase tracking-wider text-gray-500">
                                                    Beginning of conversation
                                                </span>
                                            </div>
                                        )}
                                        {messages.map((message) => {
                                            const isPlatform = message.direction === 'OUTBOUND';
                                            const isImage = message.mediaType?.startsWith('image');
                                            const isVideo = message.mediaType?.startsWith('video');
                                            const isAudio = message.mediaType?.startsWith('audio');

                                            return (
                                                <div
                                                    key={message.id}
                                                    className={`flex items-end gap-2 ${isPlatform ? 'justify-end' : 'justify-start'}`}
                                                >
                                                    {isSelectionMode && !isPlatform && (
                                                        <button onClick={() => toggleMessageSelection(message.id)} className="text-gray-400 hover:text-blue-600 mb-2">
                                                            {selectedMessageIds.has(message.id) ? <CheckSquare className="w-5 h-5 text-blue-600" /> : <Square className="w-5 h-5" />}
                                                        </button>
                                                    )}
                                                    <div className={`max-w-[330px] w-full flex ${isPlatform ? 'justify-end' : 'justify-start'} group`}>
                                                        <div
                                                            onContextMenu={(e) => {
                                                                e.preventDefault();
                                                                if (!isSelectionMode) {
                                                                    setIsSelectionMode(true);
                                                                    toggleMessageSelection(message.id);
                                                                }
                                                            }}
                                                            onClick={() => isSelectionMode && toggleMessageSelection(message.id)}
                                                            className={`rounded-xl text-sm font-medium transition-all relative ${isSelectionMode ? 'cursor-pointer hover:opacity-90' : ''} ${
                                                                message.mediaUrl ? 'p-1' : 'px-4 py-2'
                                                            } ${
                                                                isPlatform
                                                                ? 'bg-black text-white rounded-tr-none'
                                                                : 'bg-white text-gray-900 rounded-tl-none'
                                                            }`}
                                                            style={{ boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)' }}
                                                        >
                                                            {message.mediaUrl && (
                                                                <div className={`${message.content ? 'mb-1' : 'relative'}`}>
                                                                    {isImage ? (
                                                                        <a href={message.mediaUrl} target="_blank" rel="noopener noreferrer" className="block relative">
                                                                            <img src={message.mediaUrl} alt="Attached image" className="w-[322px] max-h-[322px] rounded-lg object-cover" />
                                                                        </a>
                                                                    ) : isVideo ? (
                                                                        <video src={message.mediaUrl} controls className="w-[322px] max-h-[322px] rounded-lg object-cover bg-black relative" />
                                                                    ) : isAudio ? (
                                                                        <audio src={message.mediaUrl} controls className="w-[322px] p-2" />
                                                                    ) : (
                                                                        <a href={message.mediaUrl} download target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 p-3 rounded-lg text-sm transition-colors ${isPlatform ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-50 border border-gray-100 hover:bg-gray-100'}`}>
                                                                            <div className={`p-2.5 rounded-full flex-shrink-0 ${isPlatform ? 'bg-white/20 text-white' : 'bg-blue-100 text-blue-500'}`}>
                                                                                <FileText className="w-5 h-5" />
                                                                            </div>
                                                                            <div className="flex-1 overflow-hidden">
                                                                                <div className="font-medium truncate">{message.content?.startsWith('Document') ? 'Document' : (message.content || 'File')}</div>
                                                                                <div className="text-[10px] opacity-70 uppercase tracking-wider mt-0.5">{message.mediaType?.split('/')?.[1] || 'FILE'}</div>
                                                                            </div>
                                                                            <div className={`flex-shrink-0 p-1.5 rounded-full ${isPlatform ? 'bg-white/10' : 'bg-white border border-gray-200'}`}>
                                                                                <Download className={`w-3.5 h-3.5 ${isPlatform ? 'text-white' : 'text-gray-500'}`} />
                                                                            </div>
                                                                        </a>
                                                                    )}
                                                                </div>
                                                            )}
                                                            
                                                            {message.content && (!message.mediaUrl || (isImage || isVideo)) && (
                                                                <div className={message.mediaUrl ? 'px-1 pb-0.5 pt-0.5' : ''}>
                                                                    <div className="relative">
                                                                        <span className="whitespace-pre-wrap leading-relaxed">
                                                                            {formatWhatsAppText(message.content)}
                                                                        </span>
                                                                        <span className="inline-block w-[65px] h-[1px]"></span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            
                                                            <div className={`absolute bottom-1 right-2 flex items-center justify-end gap-1 text-[9px] font-medium z-10 ${
                                                                (message.mediaUrl && !message.content && (isImage || isVideo))
                                                                ? 'bg-black/40 text-white px-1.5 py-0.5 rounded-full backdrop-blur-sm right-2 bottom-2'
                                                                : (isPlatform ? 'text-gray-400 right-3 bottom-1.5' : 'text-gray-400 right-3 bottom-1.5')
                                                            }`}>
                                                                <span>{new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                                {isPlatform && (
                                                                    message.status === 'FAILED' ? (
                                                                        <span className="text-red-400" title={message.metadata?.whatsappFailure?.message || 'Meta failed to deliver this message'}>FAILED</span>
                                                                    ) : (
                                                                        <CheckCheck className={`w-3.5 h-3.5 ${message.status === 'READ' ? 'text-blue-400' : (message.mediaUrl && !message.content ? 'text-white' : 'text-gray-500')}`} />
                                                                    )
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {isSelectionMode && isPlatform && (
                                                        <button onClick={() => toggleMessageSelection(message.id)} className="text-gray-400 hover:text-blue-600 mb-2">
                                                            {selectedMessageIds.has(message.id) ? <CheckSquare className="w-5 h-5 text-blue-600" /> : <Square className="w-5 h-5" />}
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        <div ref={messagesEndRef} />
                                    </div>

                                    {/* Input Section */}
                                    {selectedConversation.status === 'ACTIVE' ? (
                                        is24HourWindowClosed(selectedConversation.contact.lastContactedAt) ? (
                                            <div className="bg-white border-t border-gray-100 p-3">
                                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-2 flex items-start gap-2">
                                                    <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                                    <div>
                                                        <p className="text-amber-900 text-sm font-medium">24-hour service window closed</p>
                                                        <p className="text-amber-700 text-xs mt-0.5">You can only send approved template messages to this contact.</p>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2">
                                                    <select
                                                        value={selectedTemplateName}
                                                        onChange={(e) => setSelectedTemplateName(e.target.value)}
                                                        className="flex-1 bg-gray-50 border-2 border-transparent focus:border-black rounded-xl px-4 py-3 text-sm font-medium outline-none text-black"
                                                    >
                                                        <option value="">Select a template...</option>
                                                        {templates.map(t => {
                                                            const langCode = typeof t.language === 'string' ? t.language : (t.language as any).code;
                                                            return (
                                                                <option key={t.name} value={t.name}>{t.name} ({langCode})</option>
                                                            )
                                                        })}
                                                    </select>
                                                    <button
                                                        onClick={() => sendTemplate()}
                                                        disabled={sending || !selectedTemplateName}
                                                        className="px-6 bg-black text-white rounded-xl hover:bg-gray-900 disabled:bg-gray-100 disabled:text-gray-300 transition-all font-medium flex items-center justify-center min-w-[100px]"
                                                    >
                                                        {sending ? <RefreshCw className="w-5 h-5 animate-spin" /> : 'Send'}
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="bg-white border-t border-gray-100 p-3 flex flex-col gap-2">
                                                {selectedFile && (
                                                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-start justify-between">
                                                        <div className="flex items-center gap-3 overflow-hidden">
                                                            {previewUrl ? (
                                                                <img src={previewUrl} alt="Preview" className="w-12 h-12 object-cover rounded-lg border border-gray-200" />
                                                            ) : (
                                                                <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                                                                    <FileText className="w-6 h-6 text-gray-500" />
                                                                </div>
                                                            )}
                                                            <div className="overflow-hidden">
                                                                <p className="text-sm font-semibold truncate text-black">{selectedFile.name}</p>
                                                                <p className="text-xs text-gray-500">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                                                            </div>
                                                        </div>
                                                        <button 
                                                            onClick={clearFilePreview}
                                                            className="text-gray-400 hover:text-red-500 transition-colors p-1"
                                                            title="Remove file"
                                                        >
                                                            <X className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                )}
                                                <div className="max-w-full flex items-end gap-2 bg-gray-50 p-2 rounded-2xl border-2 border-transparent focus-within:border-black transition-all">
                                                    <textarea
                                                        value={newMessage}
                                                        onChange={(e) => setNewMessage(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                                e.preventDefault();
                                                                if (selectedFile) {
                                                                    sendFileMessage();
                                                                } else {
                                                                    sendMessage();
                                                                }
                                                            }
                                                        }}
                                                        placeholder={selectedFile ? "Add a caption..." : "Type your response here..."}
                                                        rows={1}
                                                        className="flex-1 bg-transparent px-3 py-2 text-sm font-medium text-black outline-none resize-none placeholder:text-gray-400"
                                                        style={{ minHeight: '40px', maxHeight: '120px' }}
                                                    />
                                                    <input
                                                        type="file"
                                                        ref={fileInputRef}
                                                        className="hidden"
                                                        onChange={handleFileUpload}
                                                    />
                                                    <button
                                                        onClick={() => fileInputRef.current?.click()}
                                                        disabled={sending}
                                                        title="Attach file"
                                                        className="w-6 h-6 text-gray-500 rounded-xl hover:bg-gray-200 disabled:opacity-30 transition-all flex items-center justify-center flex-shrink-0"
                                                    >
                                                        <Paperclip className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => setShowTemplatePicker(true)}
                                                        disabled={templates.length === 0}
                                                        title={templates.length ? 'Use an approved template' : 'No approved templates available'}
                                                        className="w-6 h-6 text-gray-500 rounded-xl hover:bg-gray-200 disabled:opacity-30 transition-all flex items-center justify-center flex-shrink-0"
                                                    >
                                                        <FileText className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={selectedFile ? sendFileMessage : sendMessage}
                                                        disabled={sending || (!newMessage.trim() && !selectedFile)}
                                                        className="w-6 h-6 bg-black text-white rounded-xl hover:bg-gray-900 disabled:bg-gray-200 disabled:text-gray-400 transition-all flex items-center justify-center flex-shrink-0"
                                                    >
                                                        {sending ? (
                                                            <RefreshCw className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <Send className="w-4 h-4 ml-0.5" />
                                                        )}
                                                    </button>
                                                </div>

                                            </div>
                                        )
                                    ) : (
                                        <div className="bg-gray-100/50 p-3 text-center">
                                            <p className="text-xs font-medium text-gray-400 uppercase tracking-widest">This conversation is {selectedConversation.status.toLowerCase()}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Right Sidebar */}
                                <div className="w-[200px] bg-white border-l border-gray-100 flex flex-col overflow-y-auto">
                                    <div className="p-3">
                                        <h4 className="text-[10px] font-semibold text-gray-400 mb-3 uppercase tracking-widest">Chat Info</h4>
                                        <div className="flex flex-col items-center mb-2 text-center">
                                            <div className="w-6 h-6 bg-gray-100 rounded-xl flex items-center justify-center mb-2">
                                                <User className="w-6 h-6 text-gray-400" />
                                            </div>
                                            <p className="font-semibold text-lg text-black leading-tight mb-1">{getContactName(selectedConversation.contact)}</p>
                                            <p className="text-sm font-medium text-gray-500">{selectedConversation.contact.phoneNumber}</p>
                                        </div>
                                        
                                        <div className="mb-2 bg-gray-50 p-3 rounded-xl">
                                            <h5 className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                                <Store className="w-3 h-3" />
                                                Store
                                            </h5>
                                            <p className="text-sm font-medium text-black">{selectedConversation.store?.name || 'Platform'}</p>
                                        </div>

                                        <div className="mb-2">
                                            <div className="flex items-center justify-between mb-2">
                                                <h5 className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Chat Tags</h5>
                                                <button onClick={() => setShowTagModal(true)} className="text-[10px] font-medium text-gray-500 hover:text-black">Manage</button>
                                            </div>
                                            <div className="flex flex-wrap gap-1.5 mb-3">
                                                {(selectedConversation.tags || []).map(tag => (
                                                    <span key={tag} style={{ backgroundColor: tagColor(tag) }} className="px-2 py-1 text-white text-[10px] font-medium rounded-md flex items-center gap-1 shadow-sm">
                                                        {tag}
                                                        <button onClick={() => removeTag(tag)} className="text-gray-400 hover:text-white transition-colors">
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    </span>
                                                ))}
                                                {(!selectedConversation.tags || selectedConversation.tags.length === 0) && (
                                                    <p className="text-xs text-gray-400 italic">No tags added</p>
                                                )}
                                            </div>
                                            <select
                                                value=""
                                                onChange={e => e.target.value && updateConversationTags([...(selectedConversation.tags || []), e.target.value])}
                                                className="w-full bg-gray-50 border border-gray-200 rounded-lg px-2 py-2 text-xs font-medium outline-none"
                                            >
                                                <option value="">Assign a tag...</option>
                                                {tagDefinitions.filter(tag => !(selectedConversation.tags || []).includes(tag.name)).map(tag => <option key={tag.id} value={tag.name}>{tag.name}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center bg-gray-50/50">
                                <div className="w-20 h-20 bg-white rounded-3xl shadow-xl flex items-center justify-center mb-2 rotate-3">
                                    <MessageSquare className="w-6 h-6 text-black" />
                                </div>
                                <h2 className="text-xl font-semibold text-black">Select a Conversation</h2>
                                <p className="text-gray-400 max-w-xs text-center mt-2 text-sm font-medium">
                                    Pick a dialogue from the sidebar to view details and reply.
                                </p>
                            </div>
                        )}
                    </>
                ) : activeTab === 'contacts' ? (
                    /* Contacts Tab */
                    <div className="flex-1 flex flex-col p-3 bg-gray-50/50 overflow-hidden">
                        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 flex-1 flex flex-col overflow-hidden">
                            <div className="p-3 border-b border-gray-100 flex items-center justify-between bg-white">
                                <h2 className="text-lg font-semibold text-black flex items-center gap-2">
                                    <Users className="w-5 h-5 text-gray-400" />
                                    Contacts Directory
                                </h2>
                                <div className="flex items-center gap-2">
                                    {selectedContactIds.length > 0 && (
                                        <div className="flex items-center gap-2 bg-gray-50 px-2 py-1.5 rounded-xl border border-gray-200">
                                            <span className="text-xs font-medium text-gray-600 px-2">{selectedContactIds.length} selected</span>
                                            <div className="w-px h-4 bg-gray-300"></div>
                                            <input
                                                type="text"
                                                placeholder="Tag name..."
                                                value={bulkTagInput}
                                                onChange={(e) => setBulkTagInput(e.target.value)}
                                                className="bg-white border-2 border-transparent focus:border-black rounded-lg px-3 py-1 text-sm font-medium outline-none w-32 shadow-sm"
                                            />
                                            <button onClick={handleBulkTag} disabled={!bulkTagInput.trim()} className="bg-black text-white px-3 py-1.5 rounded-lg text-xs font-medium disabled:bg-gray-300 transition-colors">
                                                Apply Tag
                                            </button>
                                        </div>
                                    )}
                                    <button onClick={() => setShowAddContactModal(true)} className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-gray-900 transition-colors">
                                        <Plus className="w-4 h-4" /> Add Contact
                                    </button>
                                </div>
                            </div>
                            
                            <div className="flex-1 overflow-auto">
                                <table className="w-full text-left border-collapse min-w-[800px]">
                                    <thead className="bg-gray-50/80 sticky top-0 z-10 backdrop-blur-sm">
                                        <tr>
                                            <th className="p-3 border-b border-gray-100 w-12 text-center">
                                                <input 
                                                    type="checkbox" 
                                                    className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black cursor-pointer"
                                                    onChange={(e) => {
                                                        if(e.target.checked) {
                                                            setSelectedContactIds(contacts.map(c => c.id));
                                                        } else {
                                                            setSelectedContactIds([]);
                                                        }
                                                    }}
                                                    checked={contacts.length > 0 && selectedContactIds.length === contacts.length}
                                                />
                                            </th>
                                            <th className="p-3 border-b border-gray-100 text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Name</th>
                                            <th className="p-3 border-b border-gray-100 text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Phone</th>
                                            <th className="p-3 border-b border-gray-100 text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Store</th>
                                            <th className="p-3 border-b border-gray-100 text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Tags</th>
                                            <th className="p-3 border-b border-gray-100 text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Last Contacted</th>
                                            <th className="p-3 border-b border-gray-100 text-right text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {contactsLoading ? (
                                            <tr>
                                                <td colSpan={7} className="p-8 text-center text-gray-400 text-sm font-medium">
                                                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                                                    Loading contacts...
                                                </td>
                                            </tr>
                                        ) : contacts.map(contact => (
                                            <tr key={contact.id} className="hover:bg-gray-50/50 transition-colors group">
                                                <td className="p-3 text-center">
                                                    <input 
                                                        type="checkbox"
                                                        className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity ui-checked:opacity-100"
                                                        checked={selectedContactIds.includes(contact.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) setSelectedContactIds(prev => [...prev, contact.id]);
                                                            else setSelectedContactIds(prev => prev.filter(id => id !== contact.id));
                                                        }}
                                                    />
                                                </td>
                                                <td className="p-3 text-sm font-medium text-black">{getContactName(contact)}</td>
                                                <td className="p-3 text-sm font-medium text-gray-600">{contact.phoneNumber}</td>
                                                <td className="p-3 text-sm font-medium text-gray-600">
                                                    <span className="bg-gray-100 px-2.5 py-1 rounded-lg text-xs font-medium text-gray-700">
                                                        {contact.store?.name || 'Platform'}
                                                    </span>
                                                </td>
                                                <td className="p-3">
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {contact.tags?.map(tag => (
                                                            <span key={tag} className="px-2 py-0.5 bg-black text-white text-[10px] font-medium rounded uppercase tracking-wider">
                                                                {tag}
                                                            </span>
                                                        ))}
                                                        {(!contact.tags || contact.tags.length === 0) && (
                                                            <span className="text-xs text-gray-400 italic font-medium">-</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="p-3 text-sm font-medium text-gray-500">
                                                    {contact.lastContactedAt ? (
                                                        <span className="flex items-center gap-1.5">
                                                            <Clock className="w-3.5 h-3.5 text-gray-400" />
                                                            {formatDistanceToNow(new Date(contact.lastContactedAt), { addSuffix: true })}
                                                        </span>
                                                    ) : 'Never'}
                                                </td>
                                                <td className="p-3 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setEditingContact(contact);
                                                                setNewContactName(getContactName(contact));
                                                                setEditContactModalOpen(true);
                                                            }}
                                                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-black transition-colors"
                                                            title="Edit Contact"
                                                        >
                                                            <Edit2 className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => startContactConversation(contact)}
                                                            disabled={startingConversationId === contact.id}
                                                            className="inline-flex items-center gap-2 rounded-lg bg-black px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-gray-800 disabled:cursor-wait disabled:bg-gray-400"
                                                        >
                                                            {startingConversationId === contact.id ? (
                                                                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                                                            ) : (
                                                                <MessageSquare className="h-3.5 w-3.5" />
                                                            )}
                                                            Start chat
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {contacts.length === 0 && !contactsLoading && (
                                            <tr>
                                                <td colSpan={7} className="p-12 text-center">
                                                    <Users className="w-6 h-6 mx-auto text-gray-200 mb-3" />
                                                    <p className="text-gray-400 text-sm font-medium">No contacts found</p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                ) : activeTab === 'templates' ? (
                    <div className="flex-1 flex flex-col p-3 bg-gray-50/50 overflow-hidden">
                        <div className="bg-white rounded-2xl border border-gray-100 flex-1 flex flex-col overflow-hidden">
                            <div className="p-3 border-b border-gray-100 flex items-center justify-between gap-2">
                                <div>
                                    <h2 className="text-lg font-semibold flex items-center gap-2"><FileText className="w-5 h-5 text-gray-400" /> Message Templates</h2>
                                    <p className="text-xs text-gray-400 mt-0.5">Create templates and submit them directly to Meta for approval.</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <select value={templateStoreId} onChange={e => setTemplateStoreId(e.target.value)} className="h-9 px-3 rounded-lg border border-gray-200 text-xs font-medium">
                                        <option value="PLATFORM">Platform</option>
                                        {stores.map(store => <option key={store.id} value={store.id}>{store.name}</option>)}
                                    </select>
                                    <button onClick={() => syncTemplatesWithMeta(templateStoreId)} className="w-9 h-9 rounded-lg border border-gray-200 flex items-center justify-center" title="Sync with Meta"><RefreshCw className="w-4 h-4" /></button>
                                    <button onClick={() => setShowCreateTemplateModal(true)} className="h-9 px-4 rounded-lg bg-black text-white text-xs font-medium flex items-center gap-2"><Plus className="w-4 h-4" /> Create Template</button>
                                </div>
                            </div>
                            <div className="flex-1 overflow-auto p-3">
                                {allTemplates.length === 0 ? (
                                    <div className="h-full flex flex-col items-center justify-center text-center"><FileText className="w-6 h-6 text-gray-200 mb-3" /><p className="text-sm font-medium">No templates found</p><p className="text-xs text-gray-400">Create the first template for this store.</p></div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                                        {allTemplates.map(template => (
                                            <div key={template.id} className="border border-gray-100 rounded-xl p-3 bg-white shadow-sm">
                                                <div className="flex justify-between gap-2 mb-3">
                                                    <div><p className="font-semibold text-sm">{template.name}</p><p className="text-[10px] text-gray-400 uppercase font-medium">{typeof template.language === 'string' ? template.language : template.language.code} · {template.category}</p></div>
                                                    <span className={`h-fit px-2 py-1 rounded text-[9px] font-semibold ${template.status === 'APPROVED' ? 'bg-emerald-100 text-emerald-700' : template.status === 'REJECTED' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{template.status}</span>
                                                </div>
                                                {template.header && <p className="text-xs font-semibold mb-1">{template.header}</p>}
                                                <p className="text-xs text-gray-600 whitespace-pre-wrap line-clamp-3">{template.body}</p>
                                                {template.footer && <p className="text-[10px] text-gray-400 mt-2">{template.footer}</p>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : activeTab === 'broadcasts' ? (
                    <div className="flex-1 flex flex-col p-3 bg-gray-50/50 overflow-hidden">
                        <div className="bg-white rounded-2xl border border-gray-100 flex-1 flex flex-col overflow-hidden">
                            <div className="p-3 border-b border-gray-100">
                                <h2 className="text-lg font-semibold flex items-center gap-2">Broadcast Campaign</h2>
                                <p className="text-xs text-gray-400 mt-0.5">Send a message to multiple contacts using a template.</p>
                            </div>
                            <div className="flex-1 overflow-auto p-4 flex flex-col gap-4">
                                <div className="flex flex-col gap-2">
                                    <label className="text-[10px] font-semibold text-gray-400 uppercase">1. Store Context</label>
                                    <select value={broadcastStoreId} onChange={e => { setBroadcastStoreId(e.target.value); setBroadcastTemplate(null); fetchAllTemplates(e.target.value); }} className="w-full sm:max-w-xs h-9 px-3 rounded-lg border border-gray-200 text-sm">
                                        <option value="PLATFORM">Platform</option>
                                        {stores.map(store => <option key={store.id} value={store.id}>{store.name}</option>)}
                                    </select>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label className="text-[10px] font-semibold text-gray-400 uppercase">2. Select Approved Template</label>
                                    <select value={broadcastTemplate?.id || ''} onChange={e => setBroadcastTemplate(allTemplates.find(t => t.id === e.target.value) || null)} className="w-full sm:max-w-md h-9 px-3 rounded-lg border border-gray-200 text-sm">
                                        <option value="">-- Choose a template --</option>
                                        {allTemplates.filter(t => t.status === 'APPROVED').map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                    </select>
                                    {broadcastTemplate && (
                                        <div className="mt-2 p-3 bg-gray-50 border border-gray-100 rounded-lg max-w-md">
                                            <p className="text-xs text-gray-600 whitespace-pre-wrap">{broadcastTemplate.body}</p>
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col gap-2 flex-1">
                                    <label className="text-[10px] font-semibold text-gray-400 uppercase flex justify-between max-w-2xl">
                                        <span>3. Upload Recipients (CSV Format)</span>
                                        <span className="text-gray-400 font-medium normal-case">Phone, Variable 1, Variable 2...</span>
                                    </label>
                                    <textarea 
                                        value={broadcastCsv}
                                        onChange={e => setBroadcastCsv(e.target.value)}
                                        placeholder={`254700000000, John, $50\n254711111111, Sarah, $120`}
                                        className="w-full max-w-2xl flex-1 border border-gray-200 rounded-xl p-3 text-sm font-mono whitespace-pre resize-none"
                                    ></textarea>
                                </div>
                                <div>
                                    <button 
                                        onClick={sendBroadcast} 
                                        disabled={!broadcastTemplate || !broadcastCsv.trim() || broadcastSending} 
                                        className="bg-black text-white px-6 py-2.5 rounded-lg text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                        {broadcastSending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
                                        {broadcastSending ? 'Starting Broadcast...' : 'Start Broadcast'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <FlowBuilder storeId={broadcastStoreId === 'PLATFORM' ? undefined : broadcastStoreId} />
                )}
            </div>

            {showTemplatePicker && selectedConversation && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-3">
                    <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[75vh] flex flex-col">
                        <div className="p-3 border-b border-gray-100 flex justify-between items-center"><div><h3 className="font-semibold">Use a Template</h3><p className="text-xs text-gray-400">Up to 10 approved templates for this chat</p></div><button onClick={() => setShowTemplatePicker(false)}><X className="w-5 h-5" /></button></div>
                        <div className="p-3 overflow-y-auto space-y-2">
                            {templates.map(template => (
                                <button key={template.id || `${template.name}-${typeof template.language === 'string' ? template.language : template.language.code}`} onClick={() => sendTemplate(template)} disabled={sending} className="w-full text-left border border-gray-100 hover:border-black rounded-xl p-3 disabled:opacity-50">
                                    <div className="flex justify-between gap-2"><span className="font-semibold text-sm">{template.name}</span><span className="text-[9px] font-semibold text-emerald-700 bg-emerald-100 rounded px-2 py-1">APPROVED</span></div>
                                    <p className="text-xs text-gray-500 mt-1 line-clamp-3">{template.body || 'Approved WhatsApp message template'}</p>
                                </button>
                            ))}
                            {templates.length === 0 && <p className="text-sm text-gray-400 text-center py-8">No approved templates are available.</p>}
                        </div>
                    </div>
                </div>
            )}

            {showCreateTemplateModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-3">
                    <div className="bg-white rounded-2xl w-full max-w-xl shadow-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-3 border-b border-gray-100 flex justify-between items-center"><div><h3 className="font-semibold text-lg">Create Message Template</h3><p className="text-xs text-gray-400">It will be submitted to Meta for approval.</p></div><button onClick={() => setShowCreateTemplateModal(false)}><X className="w-5 h-5" /></button></div>
                        <div className="p-3 space-y-4">
                            <div><label className="block text-[10px] font-semibold uppercase text-gray-400 mb-1">Template name</label><input value={templateForm.name} onChange={e => setTemplateForm({...templateForm, name: e.target.value})} placeholder="order_update" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" /><p className="text-[10px] text-gray-400 mt-1">Lowercase letters, numbers and underscores only.</p></div>
                            <div className="grid grid-cols-2 gap-2">
                                <div><label className="block text-[10px] font-semibold uppercase text-gray-400 mb-1">Language</label><select value={templateForm.language} onChange={e => setTemplateForm({...templateForm, language: e.target.value})} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"><option value="en">English</option><option value="en_US">English (US)</option><option value="sw">Swahili</option></select></div>
                                <div><label className="block text-[10px] font-semibold uppercase text-gray-400 mb-1">Category</label><select value={templateForm.category} onChange={e => setTemplateForm({...templateForm, category: e.target.value as Template['category']})} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"><option value="UTILITY">Utility</option><option value="MARKETING">Marketing</option><option value="AUTHENTICATION">Authentication</option></select></div>
                            </div>
                            <div><label className="block text-[10px] font-semibold uppercase text-gray-400 mb-1">Header (optional)</label><input value={templateForm.header} onChange={e => setTemplateForm({...templateForm, header: e.target.value})} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" /></div>
                            <div><label className="block text-[10px] font-semibold uppercase text-gray-400 mb-1">Body</label><textarea value={templateForm.body} onChange={e => setTemplateForm({...templateForm, body: e.target.value})} rows={6} placeholder="Hello {{1}}, your order {{2}} is ready." className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none" /><p className="text-[10px] text-gray-400 mt-1">Use sequential variables such as {'{{1}}'}, {'{{2}}'}.</p></div>
                            <div><label className="block text-[10px] font-semibold uppercase text-gray-400 mb-1">Variable examples</label><input value={templateForm.variableExamples} onChange={e => setTemplateForm({...templateForm, variableExamples: e.target.value})} placeholder="Jane, ORD-1234" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" /><p className="text-[10px] text-gray-400 mt-1">Comma-separated, in the same order as the body variables. Required when variables are used.</p></div>
                            <div><label className="block text-[10px] font-semibold uppercase text-gray-400 mb-1">Footer (optional)</label><input value={templateForm.footer} onChange={e => setTemplateForm({...templateForm, footer: e.target.value})} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" /></div>
                        </div>
                        <div className="p-3 border-t border-gray-100 flex justify-end gap-2"><button onClick={() => setShowCreateTemplateModal(false)} className="px-4 py-2 text-xs font-medium">Cancel</button><button onClick={createMessageTemplate} disabled={templateSaving || !templateForm.name.trim() || !templateForm.body.trim()} className="px-5 py-2 rounded-lg bg-black text-white text-xs font-medium disabled:opacity-40">{templateSaving ? 'Submitting…' : 'Submit for Approval'}</button></div>
                    </div>
                </div>
            )}

            {showTagModal && selectedConversation && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-3">
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
                        <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-black">Manage Tags</h3>
                                <p className="text-xs text-gray-400">{selectedConversation.store?.name || 'Platform'} tag definitions</p>
                            </div>
                            <button onClick={() => setShowTagModal(false)} className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="p-3">
                            <div className="flex flex-wrap gap-2 min-h-10 mb-2">
                                {tagDefinitions.map(tag => (
                                    <span key={tag.id} style={{ backgroundColor: tag.color }} className="px-2.5 py-1 rounded-md text-xs font-medium text-white">{tag.name}</span>
                                ))}
                                {tagDefinitions.length === 0 && <span className="text-xs text-gray-400">No tags defined yet.</span>}
                            </div>
                            <div className="flex gap-2 items-center border-t border-gray-100 pt-4">
                                <input type="color" value={newTagColor} onChange={e => setNewTagColor(e.target.value)} className="w-10 h-9 rounded cursor-pointer" title="Tag color" />
                                <input value={newTagName} onChange={e => setNewTagName(e.target.value)} onKeyDown={e => e.key === 'Enter' && createTag()} placeholder="Tag name" className="flex-1 bg-gray-50 border border-gray-200 focus:border-black rounded-lg px-3 py-2 text-sm font-medium outline-none" />
                                <button onClick={createTag} disabled={!newTagName.trim()} className="h-9 px-4 rounded-lg bg-black text-white text-xs font-medium disabled:opacity-40">Create</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Contact Modal */}
            {showAddContactModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-3 animate-in fade-in duration-200">
                    <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl">
                        <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                            <h3 className="text-xl font-semibold text-black">Add New Contact</h3>
                            <button onClick={() => setShowAddContactModal(false)} className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-black transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-3 space-y-5">
                            <div>
                                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-2">Assign to Store</label>
                                <select
                                    value={newContactStoreId}
                                    onChange={(e) => setNewContactStoreId(e.target.value)}
                                    className="w-full bg-gray-50 border-2 border-transparent focus:border-black rounded-xl px-4 py-3 text-sm font-medium outline-none cursor-pointer"
                                >
                                    <option value="">Select a store...</option>
                                    {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-2">Contact Name</label>
                                <input
                                    type="text"
                                    value={newContactName}
                                    onChange={(e) => setNewContactName(e.target.value)}
                                    placeholder="e.g. Jane Doe"
                                    className="w-full bg-gray-50 border-2 border-transparent focus:border-black rounded-xl px-4 py-3 text-sm font-medium outline-none placeholder:text-gray-400"
                                />
                            </div>
                            <div>
                                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-2">Phone Number</label>
                                <input
                                    type="tel"
                                    value={newContactPhone}
                                    onChange={(e) => setNewContactPhone(e.target.value)}
                                    placeholder="+1234567890"
                                    className="w-full bg-gray-50 border-2 border-transparent focus:border-black rounded-xl px-4 py-3 text-sm font-medium outline-none placeholder:text-gray-400"
                                />
                            </div>
                        </div>
                        <div className="p-3 border-t border-gray-100 bg-gray-50/50 flex justify-end gap-2">
                            <button onClick={() => setShowAddContactModal(false)} className="px-5 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-200 transition-colors">
                                Cancel
                            </button>
                            <button 
                                onClick={handleAddContact} 
                                disabled={!newContactStoreId || !newContactPhone}
                                className="px-6 py-2.5 rounded-xl text-sm font-medium bg-black text-white hover:bg-gray-900 transition-colors disabled:bg-gray-200 disabled:text-gray-400 shadow-sm"
                            >
                                Save Contact
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Contact Modal */}
            {editContactModalOpen && editingContact && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-3 animate-in fade-in duration-200">
                    <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl">
                        <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                            <h3 className="text-xl font-semibold text-black">Edit Contact</h3>
                            <button onClick={() => { setEditContactModalOpen(false); setEditingContact(null); }} className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-black transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-3 space-y-5">
                            <div>
                                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-2">Phone Number</label>
                                <input
                                    type="text"
                                    value={editingContact.phoneNumber}
                                    disabled
                                    className="w-full bg-gray-100 border-2 border-transparent rounded-xl px-4 py-3 text-sm font-medium outline-none text-gray-500 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-2">Contact Name</label>
                                <input
                                    type="text"
                                    value={newContactName}
                                    onChange={(e) => setNewContactName(e.target.value)}
                                    placeholder="e.g. Jane Doe"
                                    className="w-full bg-gray-50 border-2 border-transparent focus:border-black rounded-xl px-4 py-3 text-sm font-medium outline-none placeholder:text-gray-400"
                                />
                            </div>
                        </div>
                        <div className="p-3 border-t border-gray-100 bg-gray-50/50 flex justify-end gap-2">
                            <button onClick={() => { setEditContactModalOpen(false); setEditingContact(null); }} className="px-5 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-200 transition-colors">
                                Cancel
                            </button>
                            <button 
                                onClick={handleEditContact} 
                                className="px-6 py-2.5 rounded-xl text-sm font-medium bg-black text-white hover:bg-gray-900 transition-colors shadow-sm"
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Forward Modal */}
            {isForwardModalOpen && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden border border-gray-100 flex flex-col max-h-[80vh]">
                        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-white/50 backdrop-blur-xl">
                            <h3 className="font-semibold text-gray-900 tracking-tight">Forward to...</h3>
                            <button onClick={() => setIsForwardModalOpen(false)} className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-50 text-gray-400 hover:text-black hover:bg-gray-100 transition-colors">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="p-2 overflow-y-auto flex-1 bg-gray-50/30">
                            {conversations
                                .filter(c => c.status === 'ACTIVE' && c.id !== selectedConversation?.id)
                                .map(conv => (
                                    <button
                                        key={conv.id}
                                        onClick={() => setForwardTargetId(conv.id)}
                                        className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${forwardTargetId === conv.id ? 'bg-blue-50 border border-blue-200 shadow-sm' : 'hover:bg-white hover:shadow-sm border border-transparent'}`}
                                    >
                                        <div className="w-10 h-10 bg-black rounded-xl flex items-center justify-center flex-shrink-0">
                                            <User className="w-5 h-5 text-white" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-900 truncate">{getContactName(conv.contact)}</p>
                                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" /> {conv.contact.phoneNumber}</p>
                                        </div>
                                    </button>
                                ))
                            }
                            {conversations.filter(c => c.status === 'ACTIVE' && c.id !== selectedConversation?.id).length === 0 && (
                                <div className="text-center py-10">
                                    <p className="text-gray-500 font-medium text-sm">No active conversations found.</p>
                                    <p className="text-gray-400 text-xs mt-1">Users must have an open 24h window.</p>
                                </div>
                            )}
                        </div>
                        <div className="px-5 py-4 border-t border-gray-100 bg-white flex justify-end gap-2">
                            <button
                                onClick={() => setIsForwardModalOpen(false)}
                                className="px-5 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleForward}
                                disabled={!forwardTargetId || forwarding}
                                className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
                            >
                                {forwarding ? 'Forwarding...' : 'Forward'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminWhatsAppCRM;
